import { CLIENT_MONITORING_PROVIDERS, MONITORING_LABEL, isClientMonitoringProvider } from "../lib/monitoring-policy";
import { CheckCircle2, ChevronRight, ExternalLink, FileText, ShieldCheck, Upload } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { intake, type IntakeContext } from "../lib/api";
import { SignatureBg } from "../components/SignatureBg";
import { SignaturePad } from "../components/SignaturePad";

type Step = "identity" | "agreements" | "documents" | "monitoring" | "review";

const ALL_STEPS: Step[] = ["identity", "agreements", "documents", "monitoring", "review"];

export default function IntakePage() {
  const { token = "" } = useParams();
  const [ctx, setCtx] = useState<IntakeContext | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>("identity");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // identity
  const [legalName, setLegalName] = useState("");
  const [dob, setDob] = useState("");
  const [ssn, setSsn] = useState("");
  const [addr1, setAddr1] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");

  // agreements
  const [croaOk, setCroaOk] = useState(false);
  const [serviceOk, setServiceOk] = useState(false);
  const [rtcOk, setRtcOk] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [sigPng, setSigPng] = useState<string | null>(null);
  const [ctxToken, setCtxToken] = useState<string | null>(null);

  // docs
  const [govIdFile, setGovIdFile] = useState<File | null>(null);
  const [ssCardFile, setSsCardFile] = useState<File | null>(null);
  const [poaFile, setPoaFile] = useState<File | null>(null);
  const [docStatus, setDocStatus] = useState<Record<string, "idle" | "uploading" | "done" | "error">>({});

  // monitoring
  const [monProvider, setMonProvider] = useState<"my_free_score_now" | "smart_credit">("my_free_score_now");
  const [monUser, setMonUser] = useState("");
  const [monPass, setMonPass] = useState("");

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // A new token means a new client: hide the old context, wipe signing state,
    // and drop any late response from the previous token.
    let cancelled = false;
    setCtx(null); setCtxToken(null); setSigPng(null); setTypedName(""); setError(null);
    (async () => {
      try {
        const c = await intake.get(token);
        if (cancelled) return;
        setCtx(c); setCtxToken(token);
        // A prospect who picked a monitoring provider on the sign-up link
        // (/start/:slug) arrives with it seeded in the draft — start there so
        // they are not asked to enroll twice or mislabel their login.
        const seeded = c.draft?.monitoring_provider;
        if (isClientMonitoringProvider(seeded)) setMonProvider(seeded);
        setLegalName(c.client.legal_name || "");
        // jump to first incomplete step
        const s = c.steps_complete;
        if (!s.identity) setStep("identity");
        else if (!s.agreements) setStep("agreements");
        else if (!s.documents) setStep("documents");
        else if (!s.monitoring && !c.monitoring_on_file) setStep("monitoring");
        else setStep("review");
      } catch (e: any) {
        if (cancelled) return;
        setLoadError(e?.message || "This link is invalid or expired.");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Monitoring login already on file → that step is not shown at all.
  const skipMonitoring = ctx?.monitoring_on_file === true;
  const STEP_ORDER = skipMonitoring ? ALL_STEPS.filter((x) => x !== "monitoring") : ALL_STEPS;
  const stepIndex = STEP_ORDER.indexOf(step);

  // White-label: the company running this intake (token → tenant). Legal
  // contract + header render THIS company's name/address, not a hardcoded one.
  const coName = ctx?.company?.business_name || "Alief Dispute Hub";
  const coLegal = ctx?.company?.legal_entity || "{coLegal}";
  const coLogo = ctx?.company?.logo_url || null;  // no default logo image until Tanya provides one
  const coAddr1 = ctx?.company?.address_line1 || "1950 Kresge Drive";
  const coCityStateZip =
    [ctx?.company?.address_city, ctx?.company?.address_state].filter(Boolean).join(", ") +
      (ctx?.company?.address_zip ? ` ${ctx.company.address_zip}` : "") || "New Orleans, Louisiana 70117";
  const coAddrInline = `${coAddr1}, ${coCityStateZip}`;

  const ATTESTATION = useMemo(() => (
    `By typing my name below I am providing my electronic signature, acknowledging the CROA disclosure, the Service Agreement, and the 3-day Right to Cancel, and authorizing ${coLegal} to act on my behalf for credit-reporting disputes.`
  ), [coLegal]);

  async function saveIdentity() {
    setBusy(true); setError(null);
    try {
      await intake.saveIdentity(token, {
        legal_name: legalName,
        dob,
        ssn_full: ssn,
        address_line1: addr1,
        address_city: city,
        address_state: state,
        address_zip: zip,
      });
      setStep("agreements");
    } catch (e: any) { setError(e?.message || "Save failed"); }
    finally { setBusy(false); }
  }

  async function saveAgreements() {
    if (!ctx || ctxToken !== token) { setError("Still loading your intake — try again in a moment."); return; }
    if (!croaOk || !serviceOk || !rtcOk) { setError("Please acknowledge all three disclosures."); return; }
    if (!typedName.trim()) { setError("Type your full legal name to sign."); return; }
    setBusy(true); setError(null);
    try {
      await intake.saveAgreements(token, {
        croa_disclosure_signed: croaOk,
        service_agreement_signed: serviceOk,
        right_to_cancel_acknowledged: rtcOk,
        typed_name: typedName,
        attestation_text: ATTESTATION,
        signature_png: sigPng,
      });
      setStep("documents");
    } catch (e: any) { setError(e?.message || "Save failed"); }
    finally { setBusy(false); }
  }

  async function uploadOne(kind: "government_id" | "ss_card" | "proof_of_address", file: File | null): Promise<boolean> {
    if (!file) return false;
    setDocStatus(s => ({ ...s, [kind]: "uploading" }));
    try {
      await intake.uploadDocument(token, kind, file);
      setDocStatus(s => ({ ...s, [kind]: "done" }));
      return true;
    } catch {
      setDocStatus(s => ({ ...s, [kind]: "error" }));
      return false;
    }
  }

  async function saveDocuments() {
    setBusy(true); setError(null);
    try {
      // All three documents are REQUIRED — dispute letters can't be mailed
      // without them, so the intake must not advance until each is uploaded.
      const missing: string[] = [];
      if (!govIdFile && docStatus.government_id !== "done") missing.push("Government-issued ID");
      if (!ssCardFile && docStatus.ss_card !== "done") missing.push("Social Security card");
      if (!poaFile && docStatus.proof_of_address !== "done") missing.push("Proof of address");
      if (missing.length) {
        setError(`Please upload: ${missing.join(", ")}. All three documents are required before you can continue.`);
        return;
      }
      const results: boolean[] = [];
      if (govIdFile && docStatus.government_id !== "done") results.push(await uploadOne("government_id", govIdFile));
      if (ssCardFile && docStatus.ss_card !== "done") results.push(await uploadOne("ss_card", ssCardFile));
      if (poaFile && docStatus.proof_of_address !== "done") results.push(await uploadOne("proof_of_address", poaFile));
      if (results.some((ok) => !ok)) {
        setError("One or more documents failed to upload. Please retry the ones marked Failed — all three are required.");
        return;
      }
      setStep(ctx?.monitoring_on_file ? "review" : "monitoring");
    } catch (e: any) { setError(e?.message || "Upload failed"); }
    finally { setBusy(false); }
  }

  async function saveMonitoring() {
    setBusy(true); setError(null);
    try {
      await intake.saveMonitoring(token, {
        provider: monProvider,
        username: monUser,
        password: monPass,
      });
      setStep("review");
    } catch (e: any) { setError(e?.message || "Save failed"); }
    finally { setBusy(false); }
  }

  async function submit() {
    setBusy(true); setError(null);
    try {
      await intake.submit(token);
      setSubmitted(true);
    } catch (e: any) { setError(e?.message || "Submit failed"); }
    finally { setBusy(false); }
  }

  if (loadError) {
    return (
      <div className="intake-page has-signature-bg">
        <SignatureBg />
        <div className="intake-card">
          <h1>Link not available</h1>
          <p className="muted">{loadError}</p>
          <p className="muted small">If you believe this is a mistake, reply to the message we sent and we'll send a new link.</p>
        </div>
      </div>
    );
  }

  if (!ctx) {
    return <div className="intake-page has-signature-bg"><SignatureBg /><div className="intake-card"><p className="muted">Loading…</p></div></div>;
  }

  if (submitted) {
    return (
      <div className="intake-page has-signature-bg">
        <SignatureBg />
        <div className="intake-card thank-you">
          <CheckCircle2 size={48} color="#c8102e" aria-hidden="true" />
          <h1>Thank you, {ctx.client.legal_name.split(" ")[0]}.</h1>
          <p>Your intake is complete. Our team will:</p>
          <ol className="next-steps">
            <li>Verify your identity documents within 1 business day.</li>
            <li>Pull your latest credit report from {monProvider === "smart_credit" ? "SmartCredit" : "MyFreeScoreNow"}.</li>
            <li>Begin Round 1 dispute prep within 3 business days.</li>
          </ol>
          <p className="muted small">
            You'll receive a text and email when each milestone is hit. No further action is needed from you right now.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="intake-page has-signature-bg">
      <SignatureBg />
      <div className="intake-card">
        <div className="intake-header">
          {coLogo && (
            <div className="brand-mark">
              <img src={coLogo} alt="" />
            </div>
          )}
          <div>
            <strong>{coName}</strong>
            <span>Secure intake for {ctx.client.legal_name}</span>
          </div>
        </div>

        <div className="step-progress" role="list" aria-label="Intake progress">
          {STEP_ORDER.map((s, i) => (
            <div
              key={s}
              role="listitem"
              aria-current={i === stepIndex ? "step" : undefined}
              aria-label={`Step ${i + 1} of ${STEP_ORDER.length}: ${s}${i < stepIndex ? " (complete)" : i === stepIndex ? " (current)" : ""}`}
              className={`step-dot ${i <= stepIndex ? "done" : ""} ${i === stepIndex ? "current" : ""}`}
            >
              {i + 1}
            </div>
          ))}
        </div>

        {step === "identity" && (
          <>
            <h1>Identity</h1>
            <p className="muted small">We use this to verify the credit reports we pull match you, and only you.</p>
            <label className="form-field">
              <span>Legal name (as it appears on your ID)</span>
              <input value={legalName} onChange={(e) => setLegalName(e.target.value)} required />
            </label>
            <label className="form-field">
              <span>Date of birth</span>
              <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} required />
            </label>
            <label className="form-field">
              <span>Social Security Number</span>
              <input
                type="password"
                inputMode="numeric"
                pattern="\d{3}-?\d{2}-?\d{4}"
                placeholder="XXX-XX-XXXX"
                value={ssn}
                onChange={(e) => setSsn(e.target.value)}
                required
              />
              <span className="muted small">Encrypted at rest. Used only to generate dispute letters.</span>
            </label>
            <label className="form-field">
              <span>Street address</span>
              <input value={addr1} onChange={(e) => setAddr1(e.target.value)} required />
            </label>
            <div className="form-row">
              <label className="form-field">
                <span>City</span>
                <input value={city} onChange={(e) => setCity(e.target.value)} required />
              </label>
              <label className="form-field">
                <span>State</span>
                <input value={state} onChange={(e) => setState(e.target.value)} maxLength={2} required />
              </label>
              <label className="form-field">
                <span>ZIP</span>
                <input value={zip} onChange={(e) => setZip(e.target.value)} required />
              </label>
            </div>
            {error && <p className="error-text">{error}</p>}
            <button className="primary-button intake-next" onClick={saveIdentity} disabled={busy}>
              {busy ? "Saving…" : "Continue"} <ChevronRight size={16} aria-hidden="true" />
            </button>
          </>
        )}

        {step === "agreements" && (() => {
          // Today's date is captured at first render of this step and used
          // throughout the contract. Final signed_at is set server-side at
          // submit; this client-side date is what they see while signing.
          const todayStr = new Date().toLocaleDateString(undefined, {
            year: "numeric", month: "long", day: "numeric",
          });
          const clientName = (legalName || ctx.client.legal_name || "the Client").trim();
          const clientAddr = (addr1 || "").trim();
          const clientCity = (city || "").trim();
          const clientState = (state || "").trim();
          const clientZip = (zip || "").trim();
          const clientDOB = (dob || "").trim();

          // Boxed-scrollable read region — used by all three sections.
          // Paper surface — the printable-contract metaphor inside the dark
          // console. Explicit dark ink because the page text is light.
          const readBoxStyle: React.CSSProperties = {
            maxHeight: 240, overflowY: "auto", padding: 14, marginTop: 8,
            border: "1px solid var(--ne-line-2)", borderRadius: 6, background: "#f4f4f6",
            color: "#141414", fontSize: 13, lineHeight: 1.55,
          };

          return (
          <>
            <h1>Review &amp; Sign</h1>
            <p className="muted small">
              Federal law (CROA) requires we walk you through these documents before we can begin work.
              Scroll through each one, then check the box to acknowledge. Your typed name and today's
              date below count as your legal electronic signature.
            </p>

            {/* Header block — mirrors the printable contract layout */}
            <div style={{
              padding: 14, marginBottom: 16, border: "1px solid var(--ne-line-2)", borderRadius: 6,
              background: "#f4f4f6", color: "#141414", fontSize: 13, lineHeight: 1.5,
            }}>
              <p style={{ margin: 0 }}>
                <strong>{coLegal}</strong><br />
                {coAddr1}<br />
                {coCityStateZip}
              </p>
              <p style={{ marginTop: 12 }}><strong>Prepared for:</strong></p>
              <p style={{ margin: 0 }}>
                {clientName}<br />
                {clientAddr || <em className="muted">(address)</em>}<br />
                {clientCity || <em className="muted">(city)</em>}, {clientState || <em className="muted">(state)</em>} {clientZip || <em className="muted">(zip)</em>}<br />
                DOB: {clientDOB || <em className="muted">(date of birth)</em>}
              </p>
              <p style={{ marginTop: 12 }}><strong>Date:</strong> {todayStr}</p>
              <p style={{ marginTop: 12, marginBottom: 0 }}>
                <strong>The following pages contain:</strong>
              </p>
              <ol style={{ margin: "4px 0 0 20px", padding: 0 }}>
                <li>Credit Repair Service Agreement &amp; Authorization for Credit Repair Action</li>
                <li>Consumer Credit File Rights (CROA Disclosure)</li>
                <li>Right of Cancellation Notice</li>
              </ol>
            </div>

            {/* 1. SERVICE AGREEMENT + AUTHORIZATION */}
            <section className="agreement-block">
              <div className="agreement-icon" aria-hidden="true"><FileText size={18} /></div>
              <div style={{ flex: 1 }}>
                <strong>Credit Repair Service Agreement &amp; Authorization for Credit Repair Action</strong>
                <p className="muted small" style={{ fontStyle: "italic", marginTop: 4 }}>The service contract you're engaging {coLegal} under, plus the authorization that lets us act on your behalf with the credit bureaus.</p>
                <div style={readBoxStyle}>
                  <p><strong>Credit Repair Service Agreement for {clientName}</strong></p>
                  <p>I, <strong>{clientName}</strong>, hereby enter into the following agreement with {coLegal}.</p>
                  <p><strong>{coLegal} hereby agrees to perform the following:</strong></p>
                  <p>{coLegal} audits, assists, and educates clients on the process of disputing inaccurate information on the client's consumer report. {coLegal} contacts consumer reporting agencies on the client's behalf with the expectation of the consumer reporting agency modifying or deleting the item in question. {coLegal} is not a consumer reporting agency, so by law cannot delete the items being disputed, nor can make any promises besides the promise that we will put in the necessary work.</p>
                  <p>To prepare all necessary correspondence in dispute of inaccurate, erroneous, false, or obsolete information in customer's credit reports.</p>
                  <p>To review credit profile status from the credit reporting agencies such as Experian, Equifax, and TransUnion. Consulting, coaching, and monitoring services are conducted by personal meetings, webinars, video conferencing, telephone, email, or by any other form of communication during normal business hours.</p>
                  <p>In exchange, I, <strong>{clientName}</strong>, agree to pay the following fees as outlined in the following fee schedule:</p>
                  <p style={{ paddingLeft: 16 }}><strong>$67.77</strong> at the start of each new month of service.</p>

                  <hr style={{ margin: "16px 0", border: "none", borderTop: "1px dashed #c9cdd2" }} />

                  <p><strong>Authorization for Credit Repair Action</strong></p>
                  <ol style={{ paddingLeft: 18 }}>
                    <li>I, <strong>{clientName}</strong>, hereafter known as "client," hereby authorize {coLegal}, {coAddrInline}, to make, receive, sign, endorse, execute, acknowledge, deliver, and possess such applications, correspondence, contracts, or agreements as necessary to improve my credit. Such instruments in writing of whatever nature shall only be effective for any or all of the credit reporting agencies, which are TransUnion, Experian, Equifax, and any other reporting agencies or creditors as may be necessary or proper in the exercise of the rights and powers herein granted.</li>
                    <li>This authorization may be revoked by the undersigned at any time by giving written notice to the party authorized herein. Any activity made prior to revocation in reliance upon this authorization shall not constitute a breach of rights of the client. If not earlier revoked, this authorization will automatically expire twelve months from the date of signature.</li>
                    <li>The party named above to receive the information is not authorized to make any further release or disclosure of the information received. This authorization does not authorize the release or disclosure of any information except as provided herein.</li>
                    <li>I grant to {coLegal}, {coAddrInline}, authority to do, take, and perform all acts and things whatsoever requisite, proper, or necessary to be done in the exercise of repairing my credit with the three credit reporting agencies — TransUnion, Experian, Equifax — and any other reporting agencies or creditors listed, as fully for all intents and purposes as I might or could do if personally present.</li>
                    <li>I hereby release {coLegal}, {coAddrInline}, from any and all matters of actions, causes of action, suits, proceedings, debts, dues, contracts, judgments, damages, claims, and demands whatsoever in law or equity, for or by reason of any matter, cause, or thing whatsoever as based on the circumstances of this contract.</li>
                  </ol>
                </div>
                <label className="check-row" style={{ marginTop: 12 }}>
                  <input type="checkbox" checked={serviceOk} onChange={(e) => setServiceOk(e.target.checked)} />
                  <span>I have read and agree to the Service Agreement and Authorization.</span>
                </label>
              </div>
            </section>

            {/* 2. CROA DISCLOSURE */}
            <section className="agreement-block">
              <div className="agreement-icon" aria-hidden="true"><ShieldCheck size={18} /></div>
              <div style={{ flex: 1 }}>
                <strong>Consumer Credit File Rights Under State and Federal Law</strong>
                <p className="muted small" style={{ fontStyle: "italic", marginTop: 4 }}>This disclosure is required by the federal Credit Repair Organizations Act (15 U.S.C. §1679c(a)).</p>
                <div style={readBoxStyle}>
                  <p>You have a right to dispute inaccurate information in your credit report by contacting the credit bureau directly. However, neither you nor a credit repair company or credit repair organization has the right to have accurate, current, and verifiable information removed from your credit report. The credit bureau must remove accurate, negative information from your report only if it is over 7 years old. Bankruptcy information can be reported up to 10 years.</p>
                  <p>You have a right to obtain a copy of your credit report from a credit bureau. You may be charged a reasonable fee. There is no fee, however, if you have been turned down for credit, employment, insurance, or a rental dwelling because of information in your credit report within the preceding 60 days. The credit bureau must provide someone to help you interpret the information in your credit file. You are entitled to receive a free copy of your credit report if you are unemployed and intend to apply for employment in the next 60 days, if you are a recipient of public welfare assistance, or if you have reason to believe that there is inaccurate information in your credit report due to fraud.</p>
                  <p>You have a right to sue a credit repair organization that violates the Credit Repair Organization Act. This law prohibits deceptive practices by credit repair organizations.</p>
                  <p>You have the right to cancel your contract with any credit repair organization for any reason within 3 business days from the date you signed it.</p>
                  <p>Credit bureaus are required to follow reasonable procedures to ensure that the information they report is accurate. However, mistakes may occur.</p>
                  <p>You may, on your own, notify a credit bureau in writing that you dispute the accuracy of information in your credit file. The credit bureau must then reinvestigate and modify or remove inaccurate or incomplete information. The credit bureau may not charge any fee for this service. Any pertinent information and copies of all documents you have concerning an error should be given to the credit bureau.</p>
                  <p>If the credit bureau's reinvestigation does not resolve the dispute to your satisfaction, you may send a brief statement to the credit bureau to be kept in your file, explaining why you think the record is inaccurate. The credit bureau must include a summary of your statement about disputed information with any report it issues about you.</p>
                  <p>The Federal Trade Commission regulates credit bureaus and credit repair organizations. For more information contact:</p>
                  <p style={{ fontFamily: "monospace", margin: "4px 0 0 16px" }}>
                    The Public Reference Branch<br />
                    Federal Trade Commission<br />
                    Washington, D.C. 20580
                  </p>
                </div>
                <label className="check-row" style={{ marginTop: 12 }}>
                  <input type="checkbox" checked={croaOk} onChange={(e) => setCroaOk(e.target.checked)} />
                  <span>I have read and understand my Consumer Credit File Rights.</span>
                </label>
              </div>
            </section>

            {/* 3. NOTICE OF RIGHT TO CANCEL */}
            <section className="agreement-block">
              <div className="agreement-icon" aria-hidden="true"><ShieldCheck size={18} /></div>
              <div style={{ flex: 1 }}>
                <strong>Notice of Right to Cancel</strong>
                <p className="muted small" style={{ fontStyle: "italic", marginTop: 4 }}>Required by CROA §405 — your 3-business-day cancellation right.</p>
                <div style={readBoxStyle}>
                  <p>You may cancel this contract, without any penalty or obligation, at any time before midnight of the 3rd day which begins after the date the contract is signed by you.</p>
                  <p>To cancel this contract, mail or deliver a signed, dated copy of this cancellation notice, or any other written notice to:</p>
                  <p style={{ fontFamily: "monospace", margin: "4px 0 0 16px" }}>
                    {coLegal}<br />
                    {coAddr1}<br />
                    {coCityStateZip}
                  </p>
                  <p>…before midnight on the 3rd day which begins after the date you have signed this contract.</p>
                  <p>Either party may also terminate this Credit Repair Services Agreement at any time by providing written notice to the other party. If the Client decides to cancel the services after utilizing our services for months, they still retain the right to do so by providing advance written notice.</p>
                  <p style={{ marginTop: 12, fontStyle: "italic" }}>"I hereby cancel this transaction. (date) (purchaser's signature)."</p>
                  <p>Please acknowledge your receipt of this notice by electronically signing the form indicated below.</p>

                  <hr style={{ margin: "16px 0", border: "none", borderTop: "1px dashed #c9cdd2" }} />

                  <p><strong>Acknowledgment of Receipt of Notice</strong></p>
                  <p>I, <strong>{clientName}</strong>, hereby acknowledge with my digital signature receipt of the Notice of Right to Cancel. I confirm that I agree and understand what I am signing, and acknowledge that I have received a copy of my Consumer Credit File Rights.</p>
                  <p style={{ fontSize: 11, color: "#666", marginTop: 12 }}>
                    *Digital Signatures: In 2000, the U.S. Electronic Signatures in Global and National Commerce (ESIGN) Act
                    established electronic records and signatures as legally binding, having the same legal effects as
                    traditional paper documents and handwritten signatures.
                  </p>
                </div>
                <label className="check-row" style={{ marginTop: 12 }}>
                  <input type="checkbox" checked={rtcOk} onChange={(e) => setRtcOk(e.target.checked)} />
                  <span>I have received and read the Notice of Right to Cancel.</span>
                </label>
              </div>
            </section>

            {/* Signature + auto-populated date */}
            <div style={{
              marginTop: 24, padding: 16, border: "1px solid var(--ne-line-2)", borderRadius: 8,
              background: "var(--ne-surface-2)",
            }}>
              <p className="muted small" style={{ marginTop: 0 }}>{ATTESTATION}</p>
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 200px", gap: 16, alignItems: "end",
                marginTop: 12,
              }}>
                <label className="form-field" style={{ margin: 0 }}>
                  <span>Type your full legal name to sign</span>
                  <input
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder={ctx.client.legal_name}
                    style={{ fontFamily: "'Brush Script MT', cursive", fontSize: 26 }}
                  />
                </label>
                <label className="form-field" style={{ margin: 0 }}>
                  <span>Date</span>
                  <input
                    value={todayStr}
                    readOnly
                    style={{ background: "var(--ne-surface-3)", color: "var(--ne-muted)", cursor: "not-allowed" }}
                  />
                </label>
              </div>
              <SignaturePad
                key={token}
                onChange={setSigPng}
                disabled={busy}
                label="Draw your signature (optional)"
                hint="Sign here — this exact signature prints on every dispute letter we send for you"
              />
              <p className="muted small" style={{ marginBottom: 0 }}>
                Your typed name is your legal e-signature. The drawn signature is what appears on your letters, so make it the one you use.
              </p>
            </div>

            {error && <p className="error-text">{error}</p>}
            <button className="primary-button intake-next" onClick={saveAgreements} disabled={busy}>
              {busy ? "Saving…" : "Continue"} <ChevronRight size={16} aria-hidden="true" />
            </button>
          </>
          );
        })()}

        {step === "documents" && (
          <>
            <h1>Identity documents</h1>
            <p className="muted small">Every dispute letter we mail must be backed by these three documents.</p>

            {([
              { key: "government_id" as const, label: "Government-issued ID (front)", file: govIdFile, set: setGovIdFile },
              { key: "ss_card" as const, label: "Social Security card", file: ssCardFile, set: setSsCardFile },
              { key: "proof_of_address" as const, label: "Proof of address (utility, lease)", file: poaFile, set: setPoaFile },
            ]).map((d) => (
              <div key={d.key} className="doc-upload">
                <div>
                  <strong>{d.label}</strong>
                  <span className="muted small">Photo only — JPG, PNG, or HEIC. Max 10 MB. No PDFs: photos are what gets mailed with your letters, so just snap a picture with your phone.</span>
                </div>
                <div className="doc-upload-actions">
                  <label className="ghost-button">
                    <Upload size={14} aria-hidden="true" />
                    {d.file ? d.file.name : "Choose file"}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        d.set(f);
                        if (f) uploadOne(d.key, f);
                      }}
                    />
                  </label>
                  {docStatus[d.key] === "uploading" && <span className="muted small">Uploading…</span>}
                  {docStatus[d.key] === "done" && <CheckCircle2 size={16} color="#2c8a3e" aria-label="Uploaded" />}
                  {docStatus[d.key] === "error" && <span className="error-text">Failed</span>}
                </div>
              </div>
            ))}

            {error && <p className="error-text">{error}</p>}
            <button className="primary-button intake-next" onClick={saveDocuments} disabled={busy}>
              {busy ? "Saving…" : "Continue"} <ChevronRight size={16} aria-hidden="true" />
            </button>
          </>
        )}

        {step === "monitoring" && (
          <>
            <h1>Credit monitoring</h1>
            <p className="muted small">
              We need read-only access to your credit monitoring so we can pull the report each round.
              Credentials are encrypted end-to-end.
            </p>

            <label className="form-field">
              <span>Provider</span>
              {CLIENT_MONITORING_PROVIDERS.length > 1 ? (
                <select value={monProvider} onChange={(e) => setMonProvider(e.target.value as any)}>
                  {CLIENT_MONITORING_PROVIDERS.map((p) => (
                    <option key={p} value={p}>{MONITORING_LABEL[p]}</option>
                  ))}
                </select>
              ) : (
                // One provider only (monitoring-policy.ts) — no choice to make.
                <input value={MONITORING_LABEL[CLIENT_MONITORING_PROVIDERS[0]]} readOnly aria-readonly="true" />
              )}
            </label>

            {(() => {
              const provLabel = monProvider === "smart_credit" ? "SmartCredit" : "MyFreeScoreNow";
              const enrollUrl =
                monProvider === "smart_credit"
                  ? ctx.company?.smartcredit_affiliate_url
                  : ctx.company?.mfsn_affiliate_url;
              if (!enrollUrl) return null;
              return (
                <div className="intake-enroll">
                  <p>
                    <strong>New to {provLabel}?</strong> Create your account first — it only takes a
                    couple minutes. Then come back to this page and enter your login below.
                  </p>
                  <a className="primary-button" href={enrollUrl} target="_blank" rel="noopener noreferrer">
                    Sign up for {provLabel} <ExternalLink size={16} aria-hidden="true" />
                  </a>
                </div>
              );
            })()}
            <label className="form-field">
              <span>Username</span>
              <input value={monUser} onChange={(e) => setMonUser(e.target.value)} autoComplete="off" />
            </label>
            <label className="form-field">
              <span>Password</span>
              <input type="password" value={monPass} onChange={(e) => setMonPass(e.target.value)} autoComplete="off" />
            </label>

            {error && <p className="error-text">{error}</p>}
            <button className="primary-button intake-next" onClick={saveMonitoring} disabled={busy}>
              {busy ? "Saving…" : "Continue"} <ChevronRight size={16} aria-hidden="true" />
            </button>
          </>
        )}

        {step === "review" && (
          <>
            <h1>Review and submit</h1>
            <p className="muted small">Everything looks good. Submit to send your intake to our team.</p>
            <ul className="review-list">
              <li><CheckCircle2 size={16} color="#2c8a3e" aria-hidden="true" /> Identity captured</li>
              <li><CheckCircle2 size={16} color="#2c8a3e" aria-hidden="true" /> Agreements e-signed by {typedName || ctx.client.legal_name}</li>
              <li><CheckCircle2 size={16} color="#2c8a3e" aria-hidden="true" /> Documents uploaded</li>
              <li><CheckCircle2 size={16} color="#2c8a3e" aria-hidden="true" /> {skipMonitoring ? "Credit monitoring already on file" : "Monitoring credentials saved"}</li>
            </ul>
            {error && <p className="error-text">{error}</p>}
            <button className="primary-button intake-next" onClick={submit} disabled={busy}>
              {busy ? "Submitting…" : "Submit intake"} <ChevronRight size={16} aria-hidden="true" />
            </button>
          </>
        )}
      </div>

      <p className="intake-footer">
        {coLegal} · Secure intake · This link is single-use and expires {new Date(ctx.invitation.expires_at).toLocaleDateString()}.
      </p>
    </div>
  );
}
